import classNames from 'classnames';
import React, { useContext, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import _, { noop } from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Collapse,
  TextTitle,
  useCollapseState,
  ValidatedPanel,
  ValidatedFixedPanel,
  getPanelErrorProps
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { AssetGroup } from '@bingads-webui/campaign-odata-schemas';
import { FormErrorGroup, UnmappedFormErrors, andComp, propertyIs, entityIs } from '@bingads-webui-campaign-react/form-error';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { useWizardService } from '@bingads-webui-react/wizard';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { withScope } from '@bingads-webui-react/scope-service';
import {
  ImageListSummaryCardDetails,
  ValidatedImageExperienceWrapper,
  ImageListPortalContainerWithControlErrorHandling,
} from '@bingads-webui-campaign-react/pmax-image-experience';
import { Text } from '@fluentui/react';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { AppAdsPreview } from '@bingads-webui-campaign-react/unified-app-shared';

import { StepHeader } from '../../components/step-header';
import { getStyles } from './style';
import { StoreAppSelector } from './store-app-search-box';
import TextAssetsImg from './media/text.svg';
import MSStoreImg from './media/store.svg';
import ImageAssetsImg from './media/asset.svg';
import Logo from './media/logo.svg';
import { ERROR_ENTITY } from '../../model/api-errors';
import { useExitWithoutSavingConfirmation } from '../../components/use-exit-without-saving-confirmation';

const MaxImageCount = 1;
const entityIsAssetGroup = entityIs(ERROR_ENTITY.AssetGroup);
const assetGroupNameProp = andComp(entityIsAssetGroup, propertyIs('Name'));

export const AppAssetWizardInternal = ({
  data,
  error,
  onDataChange,
  scope,
}) => {
  const {
    errors = [],
  } = error || {};

  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const perfMarker = useContext(PerformanceMarkerContext);

  const showStart = true;

  const { state } = useWizardService();
  const { editingAdId } = state.context.wizardData;

  const { cid } = scope.values || {};
  const { Permissions, DynamicPermissions } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const stylingProps = useMemo(() =>
    ({ component: { placements: showStart ? 3 : 2 } }), [showStart]);

  const getClasses = useResponsiveStyles(getStyles, stylingProps);

  const { campaign, assetGroup } = data || {};
  const Headlines = (assetGroup && assetGroup.Headlines) || [];
  const ImageSPOTLIGHT = assetGroup && assetGroup.ImageSPOTLIGHT && assetGroup.ImageSPOTLIGHT.ImageList;
  const ImagePRONG = assetGroup && assetGroup.ImagePRONG && assetGroup.ImagePRONG.ImageList;
  const TransparentLogo = assetGroup && assetGroup.TransparentLogo && assetGroup.TransparentLogo.ImageList;
  const storeApp = campaign && campaign.StoreApp;
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);
  const [navigationConfirmationPrompt] = useExitWithoutSavingConfirmation(i18n);

  const appAdsAssetWizardPanels = [
    'asset-group-name',
    'app-selection',
    'app-logo',
    'short-headline',
    'large-image',
    'small-image',
  ];

  const [
    allAdSettingsSections,
    adSettingsExpandedSections,
    setAdSettingsExpandedSections,
  ] = useCollapseState(
    appAdsAssetWizardPanels /* all sections */,
    appAdsAssetWizardPanels /* initially expanded */
  );

  const [ImageSPOTLIGHTExperiencePortalContainerState, setImageSPOTLIGHTExperiencePortalContainerState] = useState({});
  const [ImagePRONGExperiencePortalContainerState, setImagePRONGExperiencePortalContainerState] = useState({});
  const [TransparentLogoExperiencePortalContainerState, setTransparentLogoExperiencePortalContainerState] = useState({});

  const ImageSPOTLIGHTContainerRef = useRef(null);
  const ImagePRONGContainerRef = useRef(null);
  const TransparentLogoContainerRef = useRef(null);

  const [previewImageSPOTLIGHTSharedState, setPreviewImageSPOTLIGHTSharedState] = useState({ previewImageId: null, cropImageObj: null });
  const [previewImagePRONGSharedState, setPreviewImagePRONGSharedState] = useState({ previewImageId: null, cropImageObj: null });
  const [previewTransparentLogoSharedState, setPreviewTransparentLogoSharedState] = useState({ previewImageId: null, cropImageObj: null });

  const portraitContainerRef = useRef(null);

  const setSelectedStoreApp = useCallback((result, newApp) => {
    if ((campaign && campaign.StoreApp === newApp) || (!campaign && !newApp)) {
      // do not trigger update if the app is the same, or there is no campaign AND no new app selected
      return;
    }
    onDataChange({
      data: {
        ...data,
        campaign: {
          ...campaign,
          StoreApp: newApp,
        },
        assetGroup,
      },
      // only valid when we have both app and asset group name configured, rest are all optional
      isValid: !!newApp && !!newApp.AppId && !!assetGroup && !!assetGroup.Name,
    });
  }, [campaign, onDataChange, data, assetGroup]);

  const onValidate = useCallback((result) => {
    onDataChange({
      data: {
        ...data,
        campaign,
        assetGroup: {
          ...assetGroup,
          ...result.data,
        },
      },
      // only valid when we have both app and asset group name configured, rest are all optional
      isValid: !!campaign && !!campaign.StoreApp && !!campaign.StoreApp.AppId && !!result.data && !!result.data.Name,
    });
  }, [data, campaign, assetGroup, onDataChange]);

  const ImageSPOTLIGHTListPortalContainer = useMemo(() => (
    <>
      <ImageListPortalContainerWithControlErrorHandling
        i18n={i18n}
        containerRef={ImageSPOTLIGHTContainerRef}
        containerState={ImageSPOTLIGHTExperiencePortalContainerState}
        isLogos={false}
      />
    </>
  ), [i18n, ImageSPOTLIGHTExperiencePortalContainerState]);

  const ImagePRONGListPortalContainer = useMemo(() => (
    <>
      <ImageListPortalContainerWithControlErrorHandling
        i18n={i18n}
        containerRef={ImagePRONGContainerRef}
        containerState={ImagePRONGExperiencePortalContainerState}
        isLogos={false}
      />
    </>
  ), [i18n, ImagePRONGExperiencePortalContainerState]);

  const TransparentLogoListPortalContainer = useMemo(() => (
    <>
      <ImageListPortalContainerWithControlErrorHandling
        i18n={i18n}
        containerRef={TransparentLogoContainerRef}
        containerState={TransparentLogoExperiencePortalContainerState}
        isLogos={false}
      />
    </>
  ), [i18n, TransparentLogoExperiencePortalContainerState]);

  const adPreview = useMemo(() => (
    <AppAdsPreview
      i18n={i18n}
      imageList={_.union(ImageSPOTLIGHT, ImagePRONG, TransparentLogo)}
      updateImageList={noop}
      shortHeadline={Headlines && Headlines.Headline0}
      businessName={storeApp && storeApp.AppTitle}
      previewImagesSharedState={previewImageSPOTLIGHTSharedState}
      setPreviewImagesSharedState={setPreviewImageSPOTLIGHTSharedState}
      hideHeaderButtons
      isAppCampaignPreview
      showStart={showStart}
    />
  ), [i18n, Headlines, showStart, ImageSPOTLIGHT, ImagePRONG, TransparentLogo, storeApp, previewImageSPOTLIGHTSharedState]);

  const validatedImageSPOTLIGHTExperienceWrapper = useMemo(() => (
    <ValidatedImageExperienceWrapper
      i18n={i18n}
      permissions={permissions}
      imagesContaineRef={ImageSPOTLIGHTContainerRef}
      portraitContainerRef={portraitContainerRef}
      isLogoEnabled={false}
      isBoostPortraitEnabled={false}
      previewImagesSharedState={previewImageSPOTLIGHTSharedState}
      setPreviewImagesSharedState={setPreviewImageSPOTLIGHTSharedState}
      updatePortalContainerState={setImageSPOTLIGHTExperiencePortalContainerState}
      validationProps={{ data: assetGroup, initialValue: assetGroup }}
      wizardPerfMarker={perfMarker}
      customMaxImageCount={MaxImageCount}
      imageListPropName="ImageSPOTLIGHT"
      imageAssetsOptional
      isStoreAppSpotLight
      isCombinedMsanAndMmaAspectRatios={false}
      isCopilotRecommendations={false}
    />
  ), [i18n, permissions, previewImageSPOTLIGHTSharedState, assetGroup, perfMarker]);

  const validatedTransparentLogoExperienceWrapper = useMemo(() => (
    <ValidatedImageExperienceWrapper
      i18n={i18n}
      permissions={permissions}
      imagesContaineRef={TransparentLogoContainerRef}
      portraitContainerRef={portraitContainerRef}
      isLogoEnabled={false}
      isBoostPortraitEnabled={false}
      previewImagesSharedState={previewTransparentLogoSharedState}
      setPreviewImagesSharedState={setPreviewTransparentLogoSharedState}
      updatePortalContainerState={setTransparentLogoExperiencePortalContainerState}
      validationProps={{ data: assetGroup, initialValue: assetGroup }}
      wizardPerfMarker={perfMarker}
      customMaxImageCount={MaxImageCount}
      imageListPropName="TransparentLogo"
      imageAssetsOptional
      isStoreAppTransparentLogo
      isCombinedMsanAndMmaAspectRatios={false}
      isCopilotRecommendations={false}
    />
  ), [i18n, permissions, previewTransparentLogoSharedState, assetGroup, perfMarker]);

  const validatedImagePRONGExperienceWrapper = useMemo(() => (
    <ValidatedImageExperienceWrapper
      i18n={i18n}
      permissions={permissions}
      imagesContaineRef={ImagePRONGContainerRef}
      portraitContainerRef={portraitContainerRef}
      isLogoEnabled={false}
      isBoostPortraitEnabled={false}
      previewImagesSharedState={previewImagePRONGSharedState}
      setPreviewImagesSharedState={setPreviewImagePRONGSharedState}
      updatePortalContainerState={setImagePRONGExperiencePortalContainerState}
      validationProps={{ data: assetGroup, initialValue: assetGroup }}
      isDisplayPlus
      wizardPerfMarker={perfMarker}
      customMaxImageCount={MaxImageCount}
      imageListPropName="ImagePRONG"
      imageAssetsOptional
      isStoreAppProng
      isCombinedMsanAndMmaAspectRatios={false}
      isCopilotRecommendations={false}
    />
  ), [i18n, permissions, previewImagePRONGSharedState, assetGroup, perfMarker]);

  const onRenderAssetGroupNameLabel = useCallback(() => (
    <TextTitle titleKey={_TL_('Asset group name')} topicKey="pop_BA_AppAds_AssetGroupName" />
  ), []);

  const onRenderHeadlineLabel = useCallback(() => (
    <TextTitle titleKey={_TL_('Short headline')} />
  ), []);

  return (
    <div className={classNames(getClasses('assetGroupStepShell'), 'asset-group-step')}>
      <>
        <ActuationGroup actuationProps={{}}>
          <AttentionBeaconGroup>
            <FormErrorGroup formErrorProps={{ errors }}>
              <ValidationGroup
                validationProps={{
                  schema: AssetGroup,
                  onValidate,
                  data: assetGroup,
                  i18n: legacyI18n,
                }}
              >
                <div className={classNames(getClasses('headerStackStyles'))}>
                  <StepHeader
                    title={editingAdId ? _TL_('Edit your app assets') : _TL_('App assets')}
                    description={_TL_("Provide the assets you want to include in your ad. We'll mix and match the most optimal combinations and show you previews of what your App Ads may look like.")}
                  />
                  <div className="server-errors">
                    <UnmappedFormErrors />
                  </div>
                </div>
                <div className={classNames(getClasses('container'))}>
                  <div className={classNames(getClasses('leftStyles'))}>
                    <Collapse allPanelItems={appAdsAssetWizardPanels} checkItems skipItemsTypeCheck activeKeys={allAdSettingsSections} setActiveKeys={setAdSettingsExpandedSections}>
                      <ValidatedFixedPanel
                        key="asset-group-name"
                        title={<TextTitle titleKey={_TL_('Asset group information')} icon={MSStoreImg} />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      >
                        <RemainingCharsValidatedTextField
                          placeholder={i18n.getString(_TL_('Enter your asset group name'))}
                          id="input-asset-group-name"
                          validationProps={{
                            field: 'Name',
                            schema: AssetGroup,
                          }}
                          i18n={i18n}
                          label={i18n.getString(_TL_('Asset group name'))}
                          onRenderLabel={onRenderAssetGroupNameLabel}
                          formErrorProps={{ filter: assetGroupNameProp }}
                        />
                      </ValidatedFixedPanel>
                    </Collapse>
                    <Collapse allPanelItems={appAdsAssetWizardPanels} checkItems skipItemsTypeCheck activeKeys={allAdSettingsSections} setActiveKeys={setAdSettingsExpandedSections}>
                      <ValidatedFixedPanel
                        key="app-selection"
                        title={<TextTitle titleKey={_TL_('Store app')} icon={MSStoreImg} />}
                      >
                        <StoreAppSelector
                          customerId={cid}
                          campaignId={campaign && campaign.Id}
                          i18n={i18n}
                          updateValidation={setSelectedStoreApp}
                          preSelectedApp={storeApp && storeApp.isPreselected ? storeApp : null}
                        />
                      </ValidatedFixedPanel>
                    </Collapse>
                    { showStart &&
                    <Collapse allPanelItems={allAdSettingsSections} checkItems skipItemsTypeCheck activeKeys={adSettingsExpandedSections} setActiveKeys={setAdSettingsExpandedSections}>
                      <ValidatedPanel
                        key="app-logo"
                        title={<TextTitle
                          titleKey={_TL_('App logo')}
                          // topicKey="pop_BA_CampaignCreate_AudAds_Images"
                          icon={Logo}
                          iconAltText={i18n.getString(_TL_('"Logo" panel\'s image'))}
                          details={<ImageListSummaryCardDetails
                            i18n={i18n}
                            images={{
                            ImageList: TransparentLogo,
                          }}
                            isLogos={false}
                          />}
                        />}
                      >
                        <Text className={classNames(getClasses('imageAssetDescription'))}>{i18n.getString(_TL_('Add one logo with a transparent background. We require PNG format and an aspect ratio of 1 : 1. The minimum dimensions are 300 x 300 pixels, and the maximum dimensions are 1000 x 1000 pixels.'), { limit: MaxImageCount })}</Text>
                        <div className={classNames(getClasses('imageExperienceStyle'))}>
                          {TransparentLogoListPortalContainer}
                        </div>
                      </ValidatedPanel>
                    </Collapse>}
                    { showStart &&
                    <Collapse allPanelItems={allAdSettingsSections} checkItems skipItemsTypeCheck activeKeys={adSettingsExpandedSections} setActiveKeys={setAdSettingsExpandedSections}>
                      <ValidatedPanel
                        key="short-headline"
                        title={<TextTitle
                          titleKey={_TL_('Short headline')}
                          // topicKey="pop_BA_AudAds_Text"
                          icon={TextAssetsImg}
                          iconAltText={i18n.getString(_TL_('"Text" panel\'s image'))}
                        />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      >
                        <RemainingCharsValidatedTextField
                          placeholder={i18n.getString(_TL_('Enter your short headline'))}
                          id="input-headline"
                          validationProps={{
                            field: 'Headlines/Headline0',
                            schema: AssetGroup,
                          }}
                          i18n={i18n}
                          label={i18n.getString(_TL_('Short headline'))}
                          onRenderLabel={onRenderHeadlineLabel}
                          formErrorProps={{ filter: propertyIs('Headlines') }}
                        />
                      </ValidatedPanel>
                    </Collapse>}
                    <Collapse allPanelItems={allAdSettingsSections} checkItems skipItemsTypeCheck activeKeys={adSettingsExpandedSections} setActiveKeys={setAdSettingsExpandedSections}>
                      <ValidatedPanel
                        key="large-image"
                        title={<TextTitle
                          titleKey={_TL_('Large image')}
                          // topicKey="pop_BA_CampaignCreate_AudAds_Images"
                          icon={ImageAssetsImg}
                          iconAltText={i18n.getString(_TL_('"Images" panel\'s image'))}
                          details={<ImageListSummaryCardDetails
                            i18n={i18n}
                            images={{
                              ImageList: ImageSPOTLIGHT,
                            }}
                            isLogos={false}
                          />}
                        />}
                      >
                        <Text className={classNames(getClasses('imageAssetDescription'))}>{i18n.getString(_TL_('You can add one large image to your ad. Its required aspect ratio is 16 : 9, the minimum dimensions are 1920 x 1080, and the maximum dimensions are 3840 x 2160.'), { limit: MaxImageCount })}</Text>
                        <div className={classNames(getClasses('imageExperienceStyle'))}>
                          {ImageSPOTLIGHTListPortalContainer}
                        </div>
                      </ValidatedPanel>
                    </Collapse>
                    <Collapse allPanelItems={allAdSettingsSections} checkItems skipItemsTypeCheck activeKeys={adSettingsExpandedSections} setActiveKeys={setAdSettingsExpandedSections}>
                      <ValidatedPanel
                        key="small-image"
                        title={<TextTitle
                          titleKey={_TL_('Small image')}
                          // topicKey="pop_BA_CampaignCreate_AudAds_Images"
                          icon={ImageAssetsImg}
                          iconAltText={i18n.getString(_TL_('"Images" panel\'s image'))}
                          details={<ImageListSummaryCardDetails
                            i18n={i18n}
                            images={{
                            ImageList: ImagePRONG,
                          }}
                            isLogos={false}
                          />}
                        />}
                      >
                        <Text className={classNames(getClasses('imageAssetDescription'))}>{i18n.getString(_TL_('You can add one small image to your ad. Its minimum dimensions are 300 x 304.'), { limit: MaxImageCount })}</Text>
                        <div className={classNames(getClasses('imageExperienceStyle'))}>
                          {ImagePRONGListPortalContainer}
                        </div>
                      </ValidatedPanel>
                    </Collapse>
                  </div>
                  <div className={classNames(getClasses('rightStyles'))}>
                    <div className={classNames(getClasses('imagePreviewCarousel'))}>
                      {adPreview}
                      {validatedImageSPOTLIGHTExperienceWrapper}
                      {validatedImagePRONGExperienceWrapper}
                      {validatedTransparentLogoExperienceWrapper}
                    </div>
                  </div>
                </div>
              </ValidationGroup>
              {navigationConfirmationPrompt}
            </FormErrorGroup>
          </AttentionBeaconGroup>
        </ActuationGroup>
      </>
    </div>
  );
};

AppAssetWizardInternal.propTypes = {
  data: PropTypes.shape({
    assetGroup: PropTypes.shape({}),
    campaign: PropTypes.shape({}),
    data: PropTypes.shape({
      campaign: PropTypes.shape({}),
      assetGroup: PropTypes.shape({}),
    }),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.string,
      aid: PropTypes.string,
    }),
  }).isRequired,
};
export const AssetGroupStepShell = withScope(AppAssetWizardInternal);
